/**
 * Direct OAuth Handler
 * 
 * Handles OAuth authentication flow directly with Okta without requiring
 * a backend /login endpoint. This implements the standard OAuth 2.0
 * Authorization Code flow entirely in the browser.
 */

import config from '../config/graphqlConfig';

/**
 * Initiate OAuth login by redirecting directly to Okta with PKCE
 */
export async function initiateOAuthLogin(): Promise<void> {

  // Generate PKCE code verifier and challenge
  const codeVerifier = generateCodeVerifier();
  const codeChallenge = await generateCodeChallenge(codeVerifier);
  
  const state = generateState();
  const nonce = generateNonce();
  
  const params = new URLSearchParams({
    client_id: config.oAuth.client_id,
    redirect_uri: config.oAuth.redirect_uri,
    response_type: 'code',
    scope: config.oAuth.scope,
    state: state,
    nonce: nonce,
    code_challenge: codeChallenge,
    code_challenge_method: 'S256'
  });

  const authUrl = `${config.oAuth.authorization}?${params.toString()}`;

  // Store state and code_verifier for verification when Okta redirects back
  // Use localStorage instead of sessionStorage to persist across redirects
  localStorage.setItem('oauth_state', state);
  localStorage.setItem('oauth_nonce', nonce);
  localStorage.setItem('pkce_code_verifier', codeVerifier);
  
  // Redirect browser to Okta
  window.location.href = authUrl;
}

/**
 * Handle OAuth callback after Okta redirects back
 * Exchanges authorization code for access token
 */
export async function handleOAuthCallback(): Promise<boolean> {
  const urlParams = new URLSearchParams(window.location.search);
  const code = urlParams.get('code');
  const state = urlParams.get('state');
  const error = urlParams.get('error');

  // Check for OAuth errors
  if (error) {
    console.error('OAuth error:', error, urlParams.get('error_description'));
    return false;
  }

  // No code means not an OAuth callback
  if (!code) {

    return false;
  }

  // Immediately clear the code from URL to prevent reuse on refresh
  const currentCode = code;
  window.history.replaceState({}, document.title, window.location.pathname);

  // Verify state for CSRF protection
  const storedState = localStorage.getItem('oauth_state');
  
  // Check if state matches what we stored, or if it's from Nike OAuth client (ngp-auth:*)
  const isNikeOAuthState = state?.startsWith('ngp-auth:');
  
  if (state && storedState && state !== storedState && !isNikeOAuthState) {
    console.error('State mismatch - possible CSRF attack');
    console.error('Expected:', storedState);
    console.error('Received:', state);
    return false;
  }
  
  if (isNikeOAuthState) {

  }

  // Exchange authorization code for access token with PKCE
  try {

    // Get the code_verifier from localStorage
    const codeVerifier = localStorage.getItem('pkce_code_verifier');
    
    if (!codeVerifier) {
      console.error('PKCE code_verifier not found in localStorage');
      console.error('This usually means the OAuth flow was interrupted or started from a different source');
      console.error('Please start fresh by clicking the login button');
      
      // Clear any stale auth data and redirect to login
      clearAuth();
      window.location.href = '/';
      return false;
    }

    const tokenResponse = await fetch(config.oAuth.token, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'Accept': 'application/json'
      },
      body: new URLSearchParams({
        grant_type: 'authorization_code',
        code: currentCode,
        redirect_uri: config.oAuth.redirect_uri,
        client_id: config.oAuth.client_id,
        code_verifier: codeVerifier
      }).toString()
    });

    if (!tokenResponse.ok) {
      const errorText = await tokenResponse.text();
      console.error('Token exchange failed:', tokenResponse.status, errorText);
      // Clear the code from URL and show login page
      window.history.replaceState({}, document.title, '/');
      return false;
    }

    const tokenData = await tokenResponse.json();

    // Store tokens
    localStorage.setItem('access_token', tokenData.access_token);
    if (tokenData.id_token) {
      localStorage.setItem('id_token', tokenData.id_token);
    }
    if (tokenData.refresh_token) {
      localStorage.setItem('refresh_token', tokenData.refresh_token);
    }

    // Decode and store user info
    if (tokenData.id_token) {
      const decoded = parseJwt(tokenData.id_token);
      const userInfo = {
        email: decoded.email || decoded.sub,
        name: decoded.name || decoded.preferred_username || 'User',
        sub: decoded.sub,
        groups: decoded.groups || []
      };
      localStorage.setItem('user_info', JSON.stringify(userInfo));

    }

    // Clean up OAuth temporary data
    localStorage.removeItem('oauth_state');
    localStorage.removeItem('oauth_nonce');
    localStorage.removeItem('pkce_code_verifier');
    
    // URL already cleaned earlier
    return true;
  } catch (error) {
    console.error('Error during token exchange:', error);
    return false;
  }
}

/**
 * Check if user is authenticated
 */
export function isAuthenticated(): boolean {
  const token = localStorage.getItem('access_token');
  if (!token) return false;

  // Check if token is expired
  try {
    const decoded = parseJwt(token);
    const now = Date.now() / 1000;
    
    if (decoded.exp && decoded.exp < now) {

      clearAuth();
      return false;
    }
    
    return true;
  } catch (error) {
    console.error('Error checking token:', error);
    return false;
  }
}

let refreshTimerId: ReturnType<typeof setTimeout> | null = null;
let sessionExpiredCallback: (() => void) | null = null;
let sessionExpiredActive = false;

export function isSessionExpiredActive(): boolean {
  return sessionExpiredActive;
}

export function setSessionExpiredActive(active: boolean): void {
  sessionExpiredActive = active;
}

export function setSessionExpiredCallback(callback: () => void): void {
  sessionExpiredCallback = callback;
}

async function refreshAccessToken(): Promise<boolean> {
  const refreshToken = localStorage.getItem('refresh_token');
  if (!refreshToken) {
    console.warn('No refresh token available');
    return false;
  }

  try {

    const tokenResponse = await fetch(config.oAuth.token, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'Accept': 'application/json'
      },
      body: new URLSearchParams({
        grant_type: 'refresh_token',
        refresh_token: refreshToken,
        client_id: config.oAuth.client_id,
        scope: config.oAuth.scope
      }).toString()
    });

    if (!tokenResponse.ok) {
      const errorText = await tokenResponse.text();
      console.error('Token refresh failed:', tokenResponse.status, errorText);
      return false;
    }

    const tokenData = await tokenResponse.json();

    localStorage.setItem('access_token', tokenData.access_token);
    if (tokenData.id_token) {
      localStorage.setItem('id_token', tokenData.id_token);
    }
    if (tokenData.refresh_token) {
      localStorage.setItem('refresh_token', tokenData.refresh_token);
    }

    return true;
  } catch (error) {
    console.error('Error refreshing token:', error);
    return false;
  }
}

function scheduleNextRefresh(): void {
  const token = localStorage.getItem('access_token');
  if (!token) return;

  try {
    const decoded = parseJwt(token);
    if (!decoded.exp) return;

    const now = Date.now() / 1000;
    const timeUntilExpiry = decoded.exp - now;
    // Refresh 5 minutes before expiry, or immediately if less than 5 min left
    const refreshIn = Math.max((timeUntilExpiry - 300) * 1000, 0);

    if (refreshTimerId) {
      clearTimeout(refreshTimerId);
    }

    refreshTimerId = setTimeout(async () => {
      const success = await refreshAccessToken();
      if (success) {
        scheduleNextRefresh();
      } else {
        console.warn('Silent refresh failed — notifying user');
        if (sessionExpiredCallback) {
          sessionExpiredCallback();
        } else {
          clearAuth();
          window.location.href = '/';
        }
      }
    }, refreshIn);
  } catch (error) {
    console.error('Error scheduling token refresh:', error);
  }
}

export function startTokenRefreshTimer(): void {

  scheduleNextRefresh();
}

export function stopTokenRefreshTimer(): void {
  if (refreshTimerId) {
    clearTimeout(refreshTimerId);
    refreshTimerId = null;

  }
}

/**
 * Clear all authentication data
 */
export function clearAuth(): void {

  // Clear all tokens
  localStorage.removeItem('access_token');
  localStorage.removeItem('id_token');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('user_info');
  localStorage.removeItem('authToken');
  
  // Clear OAuth client tokens if they exist
  localStorage.removeItem('@qr-redirect-automation-ui/tokens');
  localStorage.removeItem('@qr-redirect-automation-ui/hydrate');
  
  // Clear ALL localStorage items to be safe
  const keysToRemove = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && (key.includes('auth') || key.includes('token') || key.includes('oauth') || key.includes('user'))) {
      keysToRemove.push(key);
    }
  }
  keysToRemove.forEach(key => localStorage.removeItem(key));
  
  // Clear session storage
  sessionStorage.clear();

}

/**
 * Logout user
 */
export function logout(): void {

  clearAuth();

  window.location.href = '/';
}

/**
 * Get current user info
 */
export function getUserInfo(): any {
  const userInfoStr = localStorage.getItem('user_info');
  if (!userInfoStr) return null;
  
  try {
    return JSON.parse(userInfoStr);
  } catch (error) {
    console.error('Error parsing user info:', error);
    return null;
  }
}

/**
 * Generate random state for CSRF protection
 */
function generateState(): string {
  return Math.random().toString(36).substring(2, 15) + 
         Math.random().toString(36).substring(2, 15);
}

/**
 * Generate random nonce for replay attack protection
 */
function generateNonce(): string {
  return Math.random().toString(36).substring(2, 15) + 
         Math.random().toString(36).substring(2, 15);
}

/**
 * Parse JWT token (decode base64)
 */
function parseJwt(token: string): any {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Error parsing JWT:', error);
    return {};
  }
}

/**
 * Generate PKCE code verifier (random string)
 */
function generateCodeVerifier(): string {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return base64UrlEncode(array);
}

/**
 * Generate PKCE code challenge (SHA256 hash of verifier)
 */
async function generateCodeChallenge(verifier: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(verifier);
  const hash = await crypto.subtle.digest('SHA-256', data);
  return base64UrlEncode(new Uint8Array(hash));
}

/**
 * Base64 URL encode (for PKCE)
 */
function base64UrlEncode(array: Uint8Array): string {
  let binary = '';
  for (let i = 0; i < array.byteLength; i++) {
    binary += String.fromCharCode(array[i]);
  }
  const base64 = btoa(binary);
  return base64
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
}
